import React, { useMemo } from 'react'
import { Pie } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Box, Typography, alpha, useTheme } from '@mui/material'

ChartJS.register(ArcElement, Tooltip, Legend)

const BillingClientPieChart = ({ props: viewProps = {} }) => {
  const { totalsByClient = {}, clientSettings = {} } = viewProps
  const theme = useTheme()

  const chartData = useMemo(() => {
    const entries = Object.entries(totalsByClient).filter(([, v]) => Number(v) > 0)
    const total = entries.reduce((s, [, v]) => s + Number(v), 0)
    if (!entries.length || total <= 0) return null

    const labels = entries.map(([name]) => name)
    const data = entries.map(([, v]) => Number(v))
    const fallback = theme.palette.primary?.main || '#42a5f5'
    const backgroundColor = entries.map(([name]) =>
      clientSettings[name]?.color || alpha(fallback, 0.75)
    )

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 2,
          borderColor: theme.palette.background.paper
        }
      ]
    }
  }, [totalsByClient, clientSettings, theme.palette.background.paper, theme.palette.primary.main])

  if (!chartData) {
    return (
      <Box
        sx={{
          py: 3,
          px: 2,
          textAlign: 'center',
          borderRadius: 2,
          border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
          backgroundColor: alpha(theme.palette.action.hover, 0.04)
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          No billing totals to chart for the current filters.
        </Typography>
      </Box>
    )
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 12,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = Number(ctx.raw) || 0
            const sum = ctx.dataset.data.reduce((a, b) => a + b, 0)
            const pct = sum > 0 ? (value / sum) * 100 : 0
            return `${ctx.label}: ${value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} (${pct.toFixed(1)}%)`
          }
        }
      }
    }
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', height: 280 }}>
      <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        Share of QBO billing by client
      </Typography>
      <Box sx={{ height: 240, position: 'relative' }}>
        <Pie data={chartData} options={options} />
      </Box>
    </Box>
  )
}

export { BillingClientPieChart }
